/**
 * lib/notifications.js — Sprint 3: FCM push notifications.
 *
 * Le push sono silenziose: non rivelano contenuto.
 * Il client mostra solo "Nuovo messaggio" generico e refresha la chat.
 *
 * Privacy:
 *  - deviceId è UUID v4 random, non legato all'identità reale
 *  - utente può revocare in qualsiasi momento
 *  - in modalità stealth: nessuna notifica visibile, solo refresh in background
 *  - in modalità pure session: notifiche disabilitate (richiedono persistenza)
 */

import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { functions, firebaseApp } from './firebase';
import { isPureSessionEnabled } from './pureSession';
import { isNativeApp } from './platform';
import {
  enableNotificationsNative,
  ensureNotificationsHealthyNative,
  disableNotificationsNative,
  installForegroundMessageListenerNative,
  areNotificationsEnabledNative,
} from './notificationsNative';

const DEVICE_ID_KEY = 'nullum.deviceId';
const FCM_TOKEN_KEY = 'nullum.fcmToken';
// Intento persistente: l'utente VUOLE le notifiche. Sopravvive agli update
// (a differenza del token/SW che gli aggiornamenti possono azzerare). Serve
// per la self-healing all'apertura dell'inbox.
const NOTIF_WANTED_KEY = 'nullum.notifWanted.v1';

// VAPID key da configurare in Firebase Console → Project Settings → Cloud Messaging
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY || '';

/**
 * Ottieni (o genera) il deviceId locale.
 */
function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Richiede permesso notifiche + registra il token FCM.
 * Returns: { ok: true, token } | { ok: false, reason }
 */
export async function enableNotifications() {
  if (isPureSessionEnabled()) {
    return { ok: false, reason: 'PURE_SESSION_INCOMPATIBLE' };
  }
  // Guscio nativo (Capacitor Android): push via SDK Firebase nativo, non SW web.
  if (isNativeApp()) {
    return enableNotificationsNative();
  }
  if (!('Notification' in window)) {
    return { ok: false, reason: 'NOT_SUPPORTED' };
  }

  // Caso iOS web (Safari NON in PWA installata): iOS rifiuta sempre le push.
  // L'utente DEVE prima "Aggiungi alla schermata Home" e poi aprire la PWA da
  // li' per veder funzionare le notifiche. Lo segnaliamo PRIMA del prompt cosi'
  // il rifiuto non sembra arbitrario.
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
  if (isIOS && !isStandalone) {
    return { ok: false, reason: 'IOS_NEEDS_INSTALL' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    // Distinguiamo "mai chiesto -> default" da "negato esplicitamente"
    return { ok: false, reason: permission === 'denied' ? 'PERMISSION_DENIED' : 'PERMISSION_DISMISSED' };
  }

  try {
    const messaging = getMessaging(firebaseApp);

    // Registriamo NOI il SW di FCM, esplicitamente, con updateViaCache:'none'
    // così il browser SCAVALCA la cache HTTP/SW (in particolare la copia di
    // /firebase-messaging-sw.js che workbox potrebbe avere in precache da una
    // build precedente). Passiamo poi la registrazione a getToken: senza
    // questo, FCM tenta una sua registrazione interna che ri-passa per la
    // pipeline di workbox e può restare ancorata a una versione vecchia
    // (causa di messaging/failed-service-worker-registration).
    let swReg;
    try {
      swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
        updateViaCache: 'none',
      });
      // Forza il browser a controllare se c'è una versione nuova sul server.
      try { await swReg.update(); } catch {}
    } catch (e) {
      console.error('FCM SW register failed', e);
      return { ok: false, reason: 'ERROR', error: e?.message || String(e) };
    }

    // Forziamo la generazione di un token FRESCO: deleteToken cancella quello
    // attuale (anche eventuali "fantasmi" rimasti dopo clear-site-data o
    // reinstallazione del SW). getToken poi ne emette uno nuovo allineato al
    // SW push subscription corrente.
    try { await deleteToken(messaging); } catch { /* nessun token, ok */ }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) {
      return { ok: false, reason: 'NO_TOKEN' };
    }

    const deviceId = getOrCreateDeviceId();
    const platform = detectPlatform();

    const registerFn = httpsCallable(functions, 'registerFCMToken');
    await registerFn({ deviceId, fcmToken: token, platform });

    localStorage.setItem(FCM_TOKEN_KEY, token);
    try { localStorage.setItem(NOTIF_WANTED_KEY, '1'); } catch {}
    return { ok: true, token };
  } catch (err) {
    console.error('enableNotifications failed', err);
    return { ok: false, reason: 'ERROR', error: err.message };
  }
}

/**
 * Self-healing: se l'utente AVEVA attivato le notifiche (intento persistente)
 * e il permesso del browser e' ancora 'granted', ri-registra silenziosamente
 * SW + token + server. Risolve la "push che si spegne" dopo un aggiornamento
 * (che de-registra il SW FCM) o dopo un reload. Idempotente, no-op se non
 * dovuto. Da chiamare all'apertura dell'inbox.
 */
export async function ensureNotificationsHealthy() {
  try {
    if (isPureSessionEnabled()) return { ok: false, reason: 'PURE_SESSION' };
    if (isNativeApp()) return ensureNotificationsHealthyNative();
    if (localStorage.getItem(NOTIF_WANTED_KEY) !== '1') return { ok: false, reason: 'NOT_WANTED' };
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return { ok: false, reason: 'NO_PERMISSION' };
    }
    // permesso c'e' + l'utente le voleva → ri-arma tutto (idempotente)
    return await enableNotifications();
  } catch (e) {
    return { ok: false, reason: 'ERROR', error: e?.message };
  }
}

/**
 * Disattiva le notifiche e cancella il token server-side.
 */
export async function disableNotifications() {
  if (isNativeApp()) return disableNotificationsNative();

  const deviceId = localStorage.getItem(DEVICE_ID_KEY);

  // 1. Cancella token server-side (se c'e' deviceId)
  if (deviceId) {
    try {
      const unregisterFn = httpsCallable(functions, 'unregisterFCMToken');
      await unregisterFn({ deviceId });
    } catch (err) { console.warn('unregisterFCMToken failed', err); }
  }

  // 2. Cancella il token FCM locale.
  try {
    const messaging = getMessaging(firebaseApp);
    await deleteToken(messaging);
  } catch {}

  // 3. NUKE del SW FCM: serve a far morire la push subscription fantasma
  //    che ha causato i NotRegistered. Cosi' al prossimo Attiva ricreiamo
  //    tutto (SW + push subscription nuova + token nuovo).
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.() || [];
    for (const r of regs) {
      // Solo l'FCM, NON il workbox PWA (scope '/').
      if (r.scope?.includes('firebase-cloud-messaging-push-scope')) {
        try { await r.unregister(); } catch {}
      }
    }
  } catch (err) { console.warn('FCM SW unregister failed', err); }

  localStorage.removeItem(FCM_TOKEN_KEY);
  try { localStorage.removeItem(NOTIF_WANTED_KEY); } catch {}
  return { ok: true };
}

// ── Suono notifiche (foreground) ──────────────────────────────────────────
// Il web non permette un file-suono custom nelle notifiche di sistema; quando
// l'app e' APERTA pero' possiamo suonare noi un breve chime sintetizzato via
// Web Audio (niente file da hostare, niente problemi di CSP/cache).
const NOTIF_SOUND_KEY = 'nullum.notifSound.v1';
export function isNotifSoundEnabled() {
  try { return localStorage.getItem(NOTIF_SOUND_KEY) !== '0'; } catch { return true; }
}
export function setNotifSoundEnabled(on) {
  try { localStorage.setItem(NOTIF_SOUND_KEY, on ? '1' : '0'); } catch {}
}

let _audioCtx = null;
// Sblocca il contesto audio durante un gesto utente (i browser bloccano
// l'audio senza interazione). Da chiamare su primo tap. Niente suono.
export function primeNotificationAudio() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
  } catch {}
}
export function playNotificationSound() {
  if (!isNotifSoundEnabled()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    // Due toni brevi tipo "ding-dong" (880Hz → 1320Hz), morbidi.
    const now = ctx.currentTime;
    const notes = [{ f: 880, t: 0 }, { f: 1320, t: 0.14 }];
    for (const { f, t } of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.25, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.18);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(now + t); osc.stop(now + t + 0.2);
    }
  } catch { /* audio non disponibile: niente suono, ma nessun crash */ }
}

/**
 * Listener per messaggi in foreground (quando l'app è aperta).
 * Chiamare una volta all'avvio.
 */
export function installForegroundMessageListener(callback) {
  if (isPureSessionEnabled()) return () => {};

  // Nativo: il plugin gestisce foreground/background/tap. installForeground...Native
  // è async (ritorna Promise<cleanup>); qui restituiamo un unsubscribe sincrono
  // che attende la Promise e poi chiama il cleanup reale.
  if (isNativeApp()) {
    const p = installForegroundMessageListenerNative(callback);
    return () => { p.then((off) => { try { off?.(); } catch {} }); };
  }

  try {
    const messaging = getMessaging(firebaseApp);
    const unsubscribe = onMessage(messaging, async (payload) => {
      // 0) Segnale "in-page": dispatch un CustomEvent cosi' la UI puo' mostrare
      //    un banner "✓ push ricevuta" anche se la notifica di sistema non
      //    appare. E' diagnostica essenziale per capire dove si rompe.
      try {
        window.dispatchEvent(new CustomEvent('nullum:fcm-foreground', {
          detail: { data: payload.data || {}, notification: payload.notification || {} },
        }));
      } catch {}

      // 1) refresh dell'inbox.
      try { callback(payload.data || {}); } catch {}

      // 1b) Suono + vibrazione: l'app e' aperta, rendiamo l'avviso percepibile.
      try { playNotificationSound(); } catch {}
      try { if (navigator.vibrate) navigator.vibrate([120, 60, 120]); } catch {}

      // 2) Mostriamo NOI una notifica anche in foreground via service worker.
      //    Tentiamo SU OGNI registration disponibile: l'FCM scope, il workbox
      //    della PWA, qualsiasi cosa risponda. Senza, l'utente non vede nulla.
      try {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
        const n = payload.notification || {};
        const title = n.title || 'Nuovo messaggio';
        const opts = {
          body: n.body || 'Hai ricevuto un messaggio su Nullum.',
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: payload.data?.type === 'test_push' ? 'nullum-test' : 'nullum-message',
          renotify: true,          // ri-allerta anche con stesso tag
          requireInteraction: true, // resta finche' non la chiudi (piu' visibile)
          vibrate: [120, 60, 120],
          data: payload.data || {},
        };
        let shown = false;
        const regs = await navigator.serviceWorker?.getRegistrations?.() || [];
        for (const r of regs) {
          try { await r.showNotification(title, opts); shown = true; break; } catch {}
        }
        if (!shown && typeof Notification !== 'undefined') {
          try { new Notification(title, opts); shown = true; } catch {}
        }
        if (!shown) console.warn('foreground showNotification: no available channel');
      } catch (e) {
        console.warn('foreground showNotification failed', e);
      }
    });
    return unsubscribe;
  } catch (err) {
    console.error('installForegroundMessageListener failed', err);
    return () => {};
  }
}

function detectPlatform() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';
  return 'web';
}

export function areNotificationsEnabled() {
  if (isNativeApp()) return areNotificationsEnabledNative();
  return !!localStorage.getItem(FCM_TOKEN_KEY) &&
    typeof Notification !== 'undefined' && Notification.permission === 'granted';
}
